// Microphone permission for the web voice feature, via the native iOS wrapper.
//
// Inside the iOS WKWebView the web can't rely on navigator.mediaDevices
// getUserMedia alone to surface (and recover) the OS mic prompt, so we ask the
// native app to run the permission flow and report the result back. The web ->
// native request is fire-and-forget (iosBridge.requestIosMicrophonePermission);
// native reports the result IN via window.NineFindsNative.onPermissionChanged(
// "microphone", state), which nativeBridge.ts forwards to deliverNativeMicState
// below. Everywhere else (plain browser / Android), callers should fall back to
// getUserMedia directly — isNativeMicPermissionAvailable() tells them which path.

import { isIosWebView } from "../helpers/platform";
import { iosBridgeAvailable, requestIosMicrophonePermission } from "./iosBridge";

/** Same vocabulary the native side reports (mirrors notification states). */
export type MicPermissionState = "granted" | "denied" | "blocked" | "unknown";

function normalizeMicState(raw: string | undefined): MicPermissionState {
  return raw === "granted" || raw === "denied" || raw === "blocked"
    ? raw
    : "unknown";
}

type Listener = (state: MicPermissionState) => void;
const listeners = new Set<Listener>();

/**
 * Called by nativeBridge.onPermissionChanged when name === "microphone" — fans
 * the native-reported state out to any in-flight requestNativeMicPermission()
 * callers (and future subscribers).
 */
export function deliverNativeMicState(raw: string): void {
  const state = normalizeMicState(raw);
  listeners.forEach((l) => l(state));
}

/** True only inside the iOS wrapper with the native bridge wired — i.e. when
 *  requestNativeMicPermission() will actually do something. */
export function isNativeMicPermissionAvailable(): boolean {
  return isIosWebView() && iosBridgeAvailable();
}

/**
 * Asks the native iOS app to run the mic-permission flow (OS prompt, or open
 * Settings if already blocked) and resolves with the state native reports back.
 *
 * Outside the iOS wrapper it resolves "unknown" immediately — the caller should
 * then use navigator.mediaDevices.getUserMedia({ audio: true }) as usual.
 * A safety timeout guarantees the promise always settles.
 */
export function requestNativeMicPermission(
  timeoutMs = 60000
): Promise<MicPermissionState> {
  if (!isNativeMicPermissionAvailable()) return Promise.resolve("unknown");

  return new Promise((resolve) => {
    let settled = false;
    const finish = (state: MicPermissionState) => {
      if (settled) return;
      settled = true;
      listeners.delete(listener);
      clearTimeout(timer);
      resolve(state);
    };
    const listener: Listener = (state) => finish(state);
    listeners.add(listener);

    // Native never reporting back would otherwise hang the voice feature.
    const timer = setTimeout(() => finish("unknown"), timeoutMs);

    requestIosMicrophonePermission();
  });
}
